// This component handles Google Drive integration
// Updated to use project's authentication context instead of deprecated @supabase/auth-helpers-react
// Added onConnect callback prop for use in GoogleDriveSelector
import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { Loader2, ExternalLink, FileText } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/integrations/supabase/client'

// Google OAuth scopes needed for Drive integration
const GOOGLE_SCOPES = [
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/drive.readonly',
].join(' ')

// Google OAuth client ID from environment variable
// Updated to use Vite's environment variable naming convention
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID

interface GoogleDriveIntegrationProps {
  onConnect?: () => void;
}

export function GoogleDriveIntegration({ onConnect }: GoogleDriveIntegrationProps = {}) {
  const { user } = useAuth()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [isConnecting, setIsConnecting] = useState(false)
  
  // Query to check if user has connected Google Drive
  const { data: connection, isLoading: isLoadingConnection } = useQuery({
    queryKey: ['googleDriveConnection', user?.id],
    queryFn: async () => {
      if (!user) return null
      
      const { data, error } = await supabase
        .from('google_drive_connections')
        .select('id, google_email, created_at')
        .eq('user_id', user.id)
        .single()
        
      if (error && error.code !== 'PGRST116') {
        throw error
      }
      
      return data
    },
    enabled: !!user
  })
  
  // Effect to call onConnect if connection exists
  useEffect(() => {
    if (connection && onConnect) {
      onConnect();
    }
  }, [connection, onConnect]);
  
  // Mutation to disconnect Google Drive
  const disconnectMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('User not authenticated')
      
      const { error } = await supabase.functions.invoke('disconnect-google-drive', {
        method: 'POST',
      })
      
      if (error) throw error
      
      return true
    },
    onSuccess: () => {
      // Invalidate the connection query to refetch
      queryClient.invalidateQueries({ queryKey: ['googleDriveConnection', user?.id] })
      
      toast({
        title: 'Google Drive Disconnected',
        description: 'Your Google Drive account has been disconnected from Posit.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error disconnecting Google Drive',
        description: 'There was an error disconnecting your Google Drive account. Please try again.',
        variant: 'destructive',
      })
    },
  })
  
  // Function to initiate Google OAuth flow
  const connectGoogleDrive = () => {
    if (!GOOGLE_CLIENT_ID) {
      toast({
        title: 'Configuration Error',
        description: 'Google Drive integration is not properly configured.',
        variant: 'destructive',
      })
      return
    }
    
    setIsConnecting(true)
    
    // Generate a random state value for security
    const state = Math.random().toString(36).substring(2, 15)
    localStorage.setItem('googleOAuthState', state)
    
    // Store the onConnect callback flag in localStorage
    if (onConnect) {
      localStorage.setItem('googleOAuthCallbackNeeded', 'true')
    }
    
    // Calculate redirect URI
    const redirectUri = `${window.location.origin}/integrations/google-callback`
    
    // Build the OAuth URL
    const oauthUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth')
    oauthUrl.searchParams.append('client_id', GOOGLE_CLIENT_ID)
    oauthUrl.searchParams.append('redirect_uri', redirectUri)
    oauthUrl.searchParams.append('response_type', 'code')
    oauthUrl.searchParams.append('scope', GOOGLE_SCOPES)
    oauthUrl.searchParams.append('access_type', 'offline')
    oauthUrl.searchParams.append('prompt', 'consent')
    oauthUrl.searchParams.append('state', state)
    
    // Redirect to Google OAuth
    window.location.href = oauthUrl.toString()
  }
  
  // Function to disconnect Google Drive
  const disconnectGoogleDrive = () => {
    disconnectMutation.mutate()
  }
  
  // If loading, show a loading indicator
  if (isLoadingConnection) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }
  
  // If connected, show connection info and disconnect button
  if (connection) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Google Drive Connected</CardTitle>
          <CardDescription>
            Your Google Drive account is connected to Posit.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm">
            <FileText className="h-4 w-4 text-blue-500" />
            <span>Connected as: <strong>{connection.google_email}</strong></span>
          </div>
        </CardContent>
        <CardFooter>
          <Button 
            variant="outline" 
            onClick={disconnectGoogleDrive}
            disabled={disconnectMutation.isPending}
            className="w-full"
          >
            {disconnectMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Disconnecting...
              </>
            ) : (
              'Disconnect Google Drive'
            )}
          </Button>
        </CardFooter>
      </Card>
    )
  }
  
  // If not connected, show connect button
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Connect Google Drive</CardTitle>
        <CardDescription>
          Connect your Google Drive account to access your files in Posit.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Connecting your Google Drive allows you to:
        </p>
        <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
          <li>Browse and select files from your Google Drive</li>
          <li>Attach Google Docs, Sheets, and other files to your Posits</li>
          <li>Share Drive files with others through Posit</li>
        </ul>
      </CardContent>
      <CardFooter>
        <Button 
          onClick={connectGoogleDrive}
          disabled={isConnecting}
          className="w-full"
        >
          {isConnecting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Connecting...
            </>
          ) : (
            'Connect Google Drive'
          )}
        </Button>
      </CardFooter>
    </Card>
  )
} 